/**
 * Authenticated Fetch Composable
 *
 * Simple fetch wrapper that adds authentication headers to requests.
 * Token refresh and error handling is managed by the api.client.ts plugin.
 */

interface AuthenticatedFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: any
  headers?: Record<string, string>
}

export const useAuthenticatedFetch = () => {
  const sbAccessToken = useCookie('sb-access-token')

  /**
   * Make authenticated fetch request
   */
  const authenticatedFetch = async <T = any>(
    endpoint: string,
    options: AuthenticatedFetchOptions = {},
  ): Promise<T> => {
    const {
      method = 'GET',
      body,
      headers = {},
    } = options

    // Prepare request headers with auth token
    const requestHeaders: Record<string, string> = {
      ...headers,
      Authorization: sbAccessToken.value ? `Bearer ${sbAccessToken.value}` : '',
    }

    const response = await $fetch<T>(endpoint, {
      method,
      body,
      headers: requestHeaders,
    })

    return response
  }

  /**
   * Convenience methods for different HTTP verbs
   */
  const get = <T = any>(endpoint: string, headers?: Record<string, string>) =>
    authenticatedFetch<T>(endpoint, { method: 'GET', headers })

  const post = <T = any>(endpoint: string, body?: any, headers?: Record<string, string>) =>
    authenticatedFetch<T>(endpoint, { method: 'POST', body, headers })

  const put = <T = any>(endpoint: string, body?: any, headers?: Record<string, string>) =>
    authenticatedFetch<T>(endpoint, { method: 'PUT', body, headers })

  const patch = <T = any>(endpoint: string, body?: any, headers?: Record<string, string>) =>
    authenticatedFetch<T>(endpoint, { method: 'PATCH', body, headers })

  const del = <T = any>(endpoint: string, headers?: Record<string, string>) =>
    authenticatedFetch<T>(endpoint, { method: 'DELETE', headers })

  return {
    // Main fetch function
    authenticatedFetch,

    // Convenience methods
    get,
    post,
    put,
    patch,
    delete: del,
  }
}
